import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiPost, apiGet } from '../services/api'

interface User {
  id: number
  email: string
  name: string
  role: string
  company_id: number | null
  company?: {
    id: number
    name: string
    code: string
    enabled_modules?: Record<string, boolean>
  }
  subscription?: {
    plan: string
    status: string
    days_until_expiry: number | null
    end_date: string | null
  }
  // Eagle Vision permission fields
  ev_role?: string | null  // admin, supervisor, employee from Eagle Vision
  ev_company_id?: number | null
  ev_department_id?: number | null
  ev_designation_id?: number | null
  ev_designation_name?: string | null
  ev_permissions?: Record<string, boolean> | null
  permissions?: Record<string, Record<string, boolean>> | null
  data_scope?: Record<string, string> | null
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  isImpersonating: boolean
  originalUser: User | null
  originalTokens: { access: string; refresh: string } | null
  login: (email: string, password: string) => Promise<void>
  eagleVisionLogin: (email: string, password: string) => Promise<void>
  logout: () => void
  checkAuth: () => Promise<void>
  clearError: () => void
  impersonate: (companyId: number) => Promise<void>
  exitImpersonation: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      isImpersonating: false,
      originalUser: null,
      originalTokens: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await apiPost<{
            access_token: string
            refresh_token: string
            user: User
          }>('/auth/login', { email, password })

          localStorage.setItem('access_token', response.access_token)
          localStorage.setItem('refresh_token', response.refresh_token)

          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            isImpersonating: false,
            originalUser: null,
            originalTokens: null,
          })
        } catch (error: unknown) {
          let message = 'Login failed. Please try again.'

          // Check if it's an Axios error with response data
          if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as { response?: { status?: number; data?: { message?: string } } }
            const status = axiosError.response?.status
            const serverMessage = axiosError.response?.data?.message

            if (serverMessage) {
              message = serverMessage
            } else if (status === 401) {
              message = 'Invalid email or password. Please check your credentials.'
            } else if (status === 403) {
              message = 'Your account has been deactivated. Please contact administrator.'
            } else if (status === 404) {
              message = 'Account not found. Please check your email address.'
            } else if (status === 429) {
              message = 'Too many login attempts. Please try again later.'
            } else if (status && status >= 500) {
              message = 'Server error. Please try again later.'
            }
          } else if (error instanceof Error) {
            // Network error or other errors
            if (error.message.includes('Network Error')) {
              message = 'Unable to connect to server. Please check your internet connection.'
            }
          }

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          })
          throw error
        }
      },

      eagleVisionLogin: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await apiPost<{
            access_token: string
            refresh_token: string
            user: User
            eagle_vision_user?: object
            eagle_vision_token?: string
          }>('/auth/eagle-vision/login', { email, password })

          localStorage.setItem('access_token', response.access_token)
          localStorage.setItem('refresh_token', response.refresh_token)

          // Store Eagle Vision token for SSO portal access
          localStorage.removeItem('ev_sso_handoff_used')
          console.log('Login response keys:', Object.keys(response))
          console.log('Eagle Vision token in response:', response.eagle_vision_token ? 'YES' : 'NO')
          if (response.eagle_vision_token) {
            localStorage.setItem('ev_token', response.eagle_vision_token)
            console.log('EV token stored successfully')
          } else {
            console.warn('No eagle_vision_token in login response')
          }

          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            isImpersonating: false,
            originalUser: null,
            originalTokens: null,
          })
        } catch (error: unknown) {
          let message = 'Eagle Vision login failed. Please try again.'

          if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as { response?: { status?: number; data?: { message?: string } } }
            const status = axiosError.response?.status
            const serverMessage = axiosError.response?.data?.message

            if (serverMessage) {
              message = serverMessage
            } else if (status === 401) {
              message = 'Invalid Eagle Vision credentials.'
            } else if (status === 403) {
              message = 'Your account has been deactivated.'
            } else if (status && status >= 500) {
              message = 'Server error. Please try again later.'
            }
          } else if (error instanceof Error) {
            if (error.message.includes('Network Error')) {
              message = 'Unable to connect to server. Please check your internet connection.'
            }
          }

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          })
          throw error
        }
      },

      logout: () => {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('ev_token')  // Clear Eagle Vision SSO token
        localStorage.removeItem('ev_sso_handoff_used')
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          isImpersonating: false,
          originalUser: null,
          originalTokens: null,
        })
      },

      checkAuth: async () => {
        const token = localStorage.getItem('access_token')
        if (!token) {
          set({ isLoading: false, isAuthenticated: false })
          return
        }

        try {
          const user = await apiGet<User>('/auth/me')
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isImpersonating: false,
            originalUser: null,
            originalTokens: null,
          })
        }
      },

      clearError: () => set({ error: null }),

      impersonate: async (companyId: number) => {
        const currentState = get()

        // Store original tokens and user
        const originalAccessToken = localStorage.getItem('access_token')
        const originalRefreshToken = localStorage.getItem('refresh_token')

        try {
          const response = await apiPost<{
            access_token: string
            refresh_token: string
            user: User
            impersonated_by: number
          }>(`/companies/${companyId}/impersonate`)

          // Store new tokens
          localStorage.setItem('access_token', response.access_token)
          localStorage.setItem('refresh_token', response.refresh_token)

          set({
            user: response.user,
            isImpersonating: true,
            originalUser: currentState.user,
            originalTokens: {
              access: originalAccessToken || '',
              refresh: originalRefreshToken || '',
            },
          })
        } catch (error) {
          throw error
        }
      },

      exitImpersonation: () => {
        const { originalTokens, originalUser } = get()

        if (originalTokens && originalUser) {
          // Restore original tokens
          localStorage.setItem('access_token', originalTokens.access)
          localStorage.setItem('refresh_token', originalTokens.refresh)

          set({
            user: originalUser,
            isImpersonating: false,
            originalUser: null,
            originalTokens: null,
          })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isImpersonating: state.isImpersonating,
        originalUser: state.originalUser,
        originalTokens: state.originalTokens,
      }),
    }
  )
)

// Initialize auth check on app load
if (typeof window !== 'undefined') {
  useAuthStore.getState().checkAuth()
}
